import { gql, useMutation } from '@apollo/client';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { pipe } from 'remeda';

import { Server } from '../../../apollo/types';
import { formValuesToFormResponse, trimUnusedFormValues, validateForm } from '../../../components/forms/question/utils';
import {
  FormSubmissionCallbackResponse,
  FormSubmissionMutationData,
  FormSubmissionMutationVariables,
  FormSubmissionRequiredFields,
  FormSubmissionStatusCode,
} from '../../../components/forms/types';
import { combinedFormToQuestions } from '../../../components/forms/utils';
import { logger } from '../../../datadog/logger';
import { selectedClientInfoAtom } from '../../state';
import { GET_PATIENT_FORMS_BY_PATIENT_SHORT_ID } from '../../useGetFormsByPatientShortId';
import {
  GET_PATIENT_BY_PATIENT_SHORT_ID_GQL,
  useGetCachedPatientByShortIdCallback,
} from '../../useGetPatientByShortId';
import { formValuesForSelectedClientAtom } from '../state';
import { GroupedFormData } from '../types';

export const SUBMIT_FORM_GQL = gql`
  mutation SubmitFormResponse(
    $customerId: String!
    $formResponse: String!
    $formShortId: String!
    $isLastForm: Boolean!
    $startedAt: DateTime!
    $completedBy: CompletedBy
  ) {
    submitFormResponse(
      values: {
        customerId: $customerId
        formResponse: $formResponse
        formShortId: $formShortId
        isLastForm: $isLastForm
        startedAt: $startedAt
        completedBy: $completedBy
      }
    ) {
      formSubmission {
        formId
        formResponseId: id
      }
    }
  }
`;

export function useSubmitFormMutation() {
  return useMutation<FormSubmissionMutationData, FormSubmissionMutationVariables>(SUBMIT_FORM_GQL, {
    context: {
      server: Server.MONOLITH,
    },
    refetchQueries: [GET_PATIENT_FORMS_BY_PATIENT_SHORT_ID],
  });
}

export function useSubmitFormCallback() {
  const [submitFormMutation] = useSubmitFormMutation();
  const getFormValuesByTitle = useAtomCallback(useCallback((get) => get(formValuesForSelectedClientAtom), []));

  const getCachedPatientByShortIdCallback = useGetCachedPatientByShortIdCallback();
  const getSelectedPatientShortId = useAtomCallback(
    useCallback((get) => get(selectedClientInfoAtom)?.patientShortId, []),
  );

  return useCallback(
    async ({
      pendingForm,
      isLastForm,
      startedAt,
      completedBy,
    }: {
      pendingForm: GroupedFormData;
      startedAt: string;
      isLastForm?: boolean;
      completedBy?: 'PROVIDER' | 'CLIENT';
    }): Promise<FormSubmissionCallbackResponse> => {
      try {
        const formValuesByTitle = getFormValuesByTitle();
        const questions = combinedFormToQuestions(pendingForm);
        const pendingFormTitle = pendingForm.formTitle;
        const formValues = formValuesByTitle && formValuesByTitle[pendingFormTitle];
        const validationErrors = validateForm(questions, formValues);

        if (!formValues) {
          return {
            status: 'failure',
            code: FormSubmissionStatusCode.MISSING_FORM_VALUES,
            value: null,
          };
        }

        if (validationErrors.size) {
          return {
            status: 'failure',
            code: FormSubmissionStatusCode.VALIDATION_ERROR,
            value: validationErrors,
          };
        }

        const patientShortId = getSelectedPatientShortId();
        if (!patientShortId) {
          logger.error('Missing patient short ID when submitting form');
          return {
            status: 'failure',
            code: FormSubmissionStatusCode.MISSING_REQUIRED_FIELDS,
            value: new Set(['patientShortId']),
          };
        }

        const cachedPatientData = getCachedPatientByShortIdCallback({
          patientShortId,
        });

        const missingFields = new Set<FormSubmissionRequiredFields>();
        const customerId = cachedPatientData?.customerId;
        if (!customerId) {
          missingFields.add('customerId');
        }
        const formShortId = pendingForm.formShortId;
        if (!formShortId) {
          missingFields.add('formShortId');
        }
        if (missingFields.size) {
          logger.error('Missing required fields when submitting form', {
            missingFields,
          });
          return {
            status: 'failure',
            code: FormSubmissionStatusCode.MISSING_REQUIRED_FIELDS,
            value: missingFields,
          };
        }

        try {
          const response = await submitFormMutation({
            variables: {
              customerId: customerId!,
              formShortId,
              formResponse: pipe(
                formValues,
                (formValues) =>
                  trimUnusedFormValues(
                    formValues,
                    questions.map((q) => q.questionElement!),
                  ),
                formValuesToFormResponse,
                JSON.stringify,
              ),
              isLastForm: !!isLastForm,
              startedAt,
              completedBy,
            },
            refetchQueries: [GET_PATIENT_BY_PATIENT_SHORT_ID_GQL],
          });
          return {
            status: 'success',
            code: FormSubmissionStatusCode.SUCCESS,
            value: response.data?.submitFormResponse?.formSubmission,
          };
        } catch (e) {
          const error = e as Error;
          logger.error('Error while submitting form (provider pending forms)', {}, error);
          return {
            status: 'failure',
            code: FormSubmissionStatusCode.ERROR,
            value: error,
          };
        }
      } catch (e) {
        const error = e as Error;
        logger.error('Unexpected error while executing submit-form callback', {}, error);
        return {
          status: 'failure',
          code: FormSubmissionStatusCode.ERROR,
          value: error,
        };
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
